package no.vareflow.web

import no.vareflow.app.domain.*

// Eier-siden (Garasjestudio only): all companies with the owner's own access marked, new company,
// suspend / open again, new Firmakode, open a company; and the support inbox (two-way).
// Only owners/{uid} see it; firestore.rules enforce the same on the server.

private val ownerState = object {
    var rows: List<OwnerCompanyRow>? = null
    var loading = false
    var error: String? = null
}

fun WebApp.loadOwnerCompanies() {
    if (ownerState.loading) return
    ownerState.loading = true
    ownerState.error = null
    cloud.listAllCompanies { result ->
        ownerState.loading = false
        result.onSuccess { ownerState.rows = it }.onFailure { ownerState.error = it.message }
        requestRender()
    }
}

private fun roleText(role: String?): String = when (role?.uppercase()) {
    "ADMIN" -> "Admin"
    "LEADER" -> t("Leder", "Leader")
    "EMPLOYEE" -> t("Ansatt", "Employee")
    else -> ""
}

private fun formatDate(millis: Long?): String = millis?.let { formatDateTime(it).substringBefore(' ') } ?: "–"

fun WebApp.renderOwner(root: El, user: UserAccount) {
    val current = cloud.membership
    root.pageHeader(t("Eier · Garasjestudio", "Owner · Garasjestudio"), t("Bedrifter og support. Bare du ser denne siden.", "Companies and support. Only you see this page."))
    if (current != null) {
        root.button(t("Til ${current.companyName} ›", "To ${current.companyName} ›"), "btn outline block") {
            ownerView = false
            requestRender()
        }
    }
    val rows = ownerState.rows
    root.div("metric-row owner-metrics") {
        metricCard(t("Aktive bedrifter", "Active companies"), rows?.count { it.company.status == CompanyStatus.ACTIVE }?.toString() ?: "…")
        metricCard(t("Stengt", "Suspended"), rows?.count { it.company.status == CompanyStatus.SUSPENDED }?.toString() ?: "…")
        metricCard(t("Ulest support", "Unread support"), cloud.unreadSupportForOwner.toString())
    }

    // ---------------- visits to vareflow.no
    renderOwnerVisits(root)

    // ---------------- support
    root.h2(t("Support", "Support") + if (cloud.unreadSupportForOwner > 0) " (${cloud.unreadSupportForOwner})" else "", "section-title")
    val tickets = cloud.ownerSupport
    if (tickets.isEmpty()) root.emptyCard(t("Ingen henvendelser ennå.", "No messages yet."))
    else root.div("list") {
        tickets.take(50).forEach { ticket -> supportRow(this, ticket, asOwner = true) }
    }

    // ---------------- companies
    root.div("section-row") {
        h2(t("Bedrifter", "Companies"), "section-title")
        button(t("+ Ny bedrift", "+ New company"), "btn primary small") { showNewCompanyDialog() }
    }
    ownerState.error?.let { root.p(it, "error-text") }
    when {
        rows == null -> root.p(t("Henter bedrifter …", "Loading companies …"), "muted small")
        rows.isEmpty() -> root.emptyCard(t("Ingen bedrifter ennå.", "No companies yet."))
        else -> root.div("list") {
            rows.forEach { row -> ownerCompanyCard(this, row) }
        }
    }
    root.p(t("«Tilgang» betyr at du selv er med i bedriften og kan åpne den. Uten tilgang ser du bare navn, kode og status.",
        "«Access» means you are a member of the company yourself and can open it. Without access you only see name, code and status."), "muted tiny")
}

private fun WebApp.ownerCompanyCard(parent: El, row: OwnerCompanyRow) {
    val app = this
    val c = row.company
    val suspended = c.status == CompanyStatus.SUSPENDED
    val isOpenNow = cloud.membership?.companyId == c.id
    parent.div("card owner-company" + if (row.myRole != null) " has-access" else " no-access") {
        div("row") {
            div("company-monogram", c.monogram)
            div("grow") {
                div("card-title", c.name)
                div("muted small", t("Firmakode", "Company code") + " ${c.code.ifBlank { "–" }} · " + t("opprettet", "created") + " ${formatDate(row.createdAt)}")
            }
            statusBadge(if (suspended) t("STENGT", "SUSPENDED") else t("AKTIV", "ACTIVE"), if (suspended) Tone.RED else Tone.GREEN)
        }
        div("chip-row wrap") {
            if (row.myRole != null) span("access-chip yes", "✓ " + t("Tilgang", "Access") + ": " + roleText(row.myRole) + if (isOpenNow) " · " + t("åpen nå", "open now") else "")
            else span("access-chip no", t("Ingen tilgang", "No access"))
        }
        if (suspended) {
            val why = when (row.suspendedReason) {
                "PAYMENT" -> t("Betaling mangler", "Payment missing")
                "" -> ""
                else -> t("Annet", "Other")
            }
            if (why.isNotBlank() || row.suspendedNote.isNotBlank()) div("warn-text small", listOf(why, row.suspendedNote).filter { it.isNotBlank() }.joinToString(" · "))
        }
        div("button-row") {
            if (row.myRole != null && !suspended) button(if (isOpenNow) t("Åpen", "Open") else t("Åpne", "Open"), "btn primary small", enabled = !isOpenNow) {
                app.flushPendingCounts()
                app.cloud.openCompany(c.id) { result ->
                    result.onSuccess { m ->
                        app.activateCloud(m, app.cloud.isRemembered())
                        app.ownerView = false
                        app.toast(t("${m.companyName} er åpnet.", "${m.companyName} is open."))
                    }.onFailure { app.toast(it.message ?: t("Kunne ikke åpne bedriften.", "Could not open the company.")) }
                }
            }
            if (suspended) button(t("Aktiver", "Activate"), "btn outline small") { app.confirmSuspend(row, suspend = false) }
            else button(t("Steng", "Suspend"), "btn text small danger-text") { app.confirmSuspend(row, suspend = true) }
            button(t("Ny Firmakode", "New company code"), "btn text small") { app.confirmNewCode(row) }
        }
    }
}

private fun WebApp.confirmSuspend(row: OwnerCompanyRow, suspend: Boolean) {
    var reason = "PAYMENT"
    var note = ""
    var busy = false
    var error: String? = null
    val name = row.company.name
    openDialog(if (suspend) t("Steng $name?", "Suspend $name?") else t("Aktiver $name?", "Activate $name?")) { close ->
        if (suspend) {
            p(t("Er du sikker? Ingen i $name kan logge inn mens bedriften er stengt. Dataene beholdes, og du kan aktivere den igjen når som helst.",
                "Are you sure? Nobody in $name can log in while the company is suspended. The data is kept, and you can activate it again at any time."))
            div("strong small", t("Årsak", "Reason"))
            div("chip-row wrap") {
                chip(t("Betaling mangler", "Payment missing"), reason == "PAYMENT") { reason = "PAYMENT"; requestRender() }
                chip(t("Annet", "Other"), reason == "OTHER") { reason = "OTHER"; requestRender() }
            }
            field(t("Notat (bare for deg)", "Note (only for you)"), note, "own-susp-note") { note = it }
            if (cloud.membership?.companyId == row.company.id) p(t("Du har denne bedriften åpen nå; du blir også stengt ute av den.", "You have this company open now; you will be locked out of it too."), "warn-text small")
        } else {
            p(t("$name blir åpen igjen, og alle kan logge inn som før.", "$name opens again, and everyone can log in as before."))
        }
        errorText(error)
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text", enabled = !busy) { close() }
            button(if (suspend) t("Ja, steng", "Yes, suspend") else t("Ja, aktiver", "Yes, activate"), if (suspend) "btn danger" else "btn primary", enabled = !busy) {
                busy = true
                requestRender()
                cloud.setCompanySuspended(row.company.id, suspend, reason, note) { result ->
                    busy = false
                    result.onSuccess {
                        close()
                        toast(if (suspend) t("$name er stengt.", "$name is suspended.") else t("$name er aktiv igjen.", "$name is active again."))
                        loadOwnerCompanies()
                    }.onFailure { error = it.message; requestRender() }
                }
            }
        }
    }
}

private fun WebApp.confirmNewCode(row: OwnerCompanyRow) {
    var busy = false
    var error: String? = null
    var newCode: String? = null
    val name = row.company.name
    openDialog(t("Ny Firmakode for $name?", "New company code for $name?")) { close ->
        newCode?.let { code ->
            p(t("Ny Firmakode:", "New company code:"))
            div("big-code", code)
            p(t("Gi den nye koden til de som logger inn med Firmakode (uten e-post).", "Give the new code to the people who log in with the company code (without e-mail)."), "muted small")
            dialogActions { button(t("Ferdig", "Done"), "btn primary") { close() } }
            return@openDialog
        }
        p(t("Den gamle koden (${row.company.code}) slutter å virke med en gang. De som logger inn med Firmakode, må få den nye koden.",
            "The old code (${row.company.code}) stops working at once. People who log in with the company code must get the new one."))
        errorText(error)
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text", enabled = !busy) { close() }
            button(if (busy) t("Lager …", "Making …") else t("Lag ny kode", "Make new code"), "btn primary", enabled = !busy) {
                busy = true
                requestRender()
                cloud.changeCompanyCode(row.company.id) { result ->
                    busy = false
                    result.onSuccess { newCode = it; loadOwnerCompanies() }.onFailure { error = it.message }
                    requestRender()
                }
            }
        }
    }
}

// ---------------------------------------------------------------- support (both sides)

private fun WebApp.supportRow(parent: El, ticket: SupportTicket, asOwner: Boolean) {
    val unread = if (asOwner) ticket.unreadByOwner else ticket.unreadByUser
    parent.el("button", "card nav-card support-row" + if (unread) " unread" else "") {
        attr("type", "button")
        if (unread) span("unread-dot").attr("aria-label", t("Ulest", "Unread"))
        div("grow") {
            div("card-title", ticket.subject)
            val who = if (asOwner) "${ticket.companyName} · ${ticket.userName} · " else ""
            div("muted small", who + ticket.category.label + " · " + (if (ticket.updatedAt > 0) formatDateTime(ticket.updatedAt) else ""))
        }
        statusBadge(ticket.status.label, when (ticket.status) {
            SupportStatus.OPEN -> Tone.AMBER
            SupportStatus.ANSWERED -> Tone.GREEN
            SupportStatus.CLOSED -> Tone.GREY
        })
        onClick { showSupportThread(ticket.id, asOwner) }
    }
}

/** One conversation: the messages, an answer field, and (owner) close / open again. */
fun WebApp.showSupportThread(ticketId: String, asOwner: Boolean) {
    var messages: List<SupportMessage>? = null
    var reply = ""
    var busy = false
    var error: String? = null
    var stop: (() -> Unit)? = null
    var myDialog: Dialog? = null
    fun current(): SupportTicket? = (if (asOwner) cloud.ownerSupport else cloud.mySupport).firstOrNull { it.id == ticketId }
    val ticket0 = current() ?: return
    cloud.markSupportRead(ticket0, asOwner)
    stop = cloud.listenSupportMessages(ticketId) { list ->
        // Closed another way (outside click): stop listening, and do not mark new answers as read.
        if (myDialog != null && dialog !== myDialog) { stop?.invoke(); stop = null; return@listenSupportMessages }
        messages = list
        current()?.let { cloud.markSupportRead(it, asOwner) }
        requestRender()
    }
    openDialog(ticket0.subject) { close ->
        val ticket = current() ?: ticket0
        val closeAll = { stop?.invoke(); stop = null; close() }
        div("muted small", (if (asOwner) "${ticket.companyName} · ${ticket.userName} (${ticket.userEmail}) · " else "") +
            ticket.category.label + " · " + ticket.status.label)
        if (asOwner) div("muted tiny", "${ticket.platform} ${ticket.appVersion} · ${ticket.language}")
        div("support-thread") {
            attr("data-scroll-key", "support-thread")
            val list = messages
            if (list == null) p(t("Henter …", "Loading …"), "muted small")
            else list.forEach { m ->
                val mine = m.fromOwner == asOwner
                div("bubble " + if (mine) "mine" else "theirs") {
                    div("bubble-head", (if (m.fromOwner) "Garasjestudio" else m.authorName) + (if (m.createdAt > 0) " · " + formatDateTime(m.createdAt) else ""))
                    m.text.split("\n").forEach { line -> div("bubble-line", line) }
                }
            }
        }
        if (ticket.status == SupportStatus.CLOSED && !asOwner) {
            p(t("Saken er lukket. Skriv en ny melding for å åpne den igjen.", "The case is closed. Write a new message to open it again."), "muted small")
        }
        el("label", "field") {
            span("field-label", t("Svar", "Reply"))
            el("textarea", "input textarea") {
                attr("data-key", "support-reply")
                attr("rows", "3")
                node.value = reply
                on("input") { reply = node.value as String }
            }
        }
        errorText(error)
        dialogActions {
            if (asOwner) {
                if (ticket.status == SupportStatus.CLOSED) button(t("Åpne igjen", "Reopen"), "btn text", enabled = !busy) {
                    cloud.setSupportClosed(ticket, false) { requestRender() }
                } else button(t("Lukk saken", "Close case"), "btn text", enabled = !busy) {
                    cloud.setSupportClosed(ticket, true) { closeAll(); toast(t("Saken er lukket.", "The case is closed.")) }
                }
            }
            span("spacer")
            button(t("Lukk", "Close"), "btn text") { closeAll() }
            button(if (busy) t("Sender …", "Sending …") else t("Send", "Send"), "btn primary", enabled = !busy) {
                error = SupportRules.problem(null, reply)
                if (error != null) { requestRender(); return@button }
                busy = true
                requestRender()
                cloud.replySupport(ticket, reply, asOwner) { result ->
                    busy = false
                    result.onSuccess { reply = "" }.onFailure { error = it.message }
                    requestRender()
                }
            }
        }
    }
    myDialog = dialog
}

/** Mer → Support: my conversations and a new one. */
fun WebApp.renderSupport(root: El) {
    root.button(t("‹ Mer", "‹ More"), "btn text back") { moreView = "main"; requestRender() }
    root.pageHeader(t("Support", "Support"), t("Skriv til Garasjestudio om feil, spørsmål eller ønsker.", "Write to Garasjestudio about bugs, questions or requests."))
    root.button(t("+ Ny melding", "+ New message"), "btn primary") { showNewSupportDialog() }
    val mine = cloud.mySupport
    if (mine.isEmpty()) root.emptyCard(t("Du har ikke skrevet til support ennå.", "You have not written to support yet."))
    else root.div("list") { mine.forEach { supportRow(this, it, asOwner = false) } }
    root.p(t("Med meldingen sendes bedrift, navn, e-post, app-versjon og språk, så vi slipper å spørre.",
        "The message includes company, name, e-mail, app version and language, so we don't have to ask."), "muted tiny")
}

fun WebApp.showNewSupportDialog() {
    var category = SupportCategory.QUESTION
    var subject = ""
    var text = ""
    var busy = false
    var error: String? = null
    openDialog(t("Ny melding til support", "New message to support")) { close ->
        div("chip-row wrap") {
            SupportCategory.entries.forEach { c -> chip(c.label, category == c) { category = c; requestRender() } }
        }
        field(t("Emne", "Subject"), subject, "sup-subject") { subject = it }
        el("label", "field") {
            span("field-label", t("Melding", "Message"))
            el("textarea", "input textarea") {
                attr("data-key", "sup-text")
                attr("rows", "5")
                node.value = text
                on("input") { text = node.value as String }
            }
        }
        errorText(error)
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text", enabled = !busy) { close() }
            button(if (busy) t("Sender …", "Sending …") else t("Send", "Send"), "btn primary", enabled = !busy) {
                error = SupportRules.problem(subject, text)
                if (error != null) { requestRender(); return@button }
                busy = true
                requestRender()
                cloud.createSupportTicket(category, subject, text) { result ->
                    busy = false
                    result.onSuccess { close(); toast(t("Meldingen er sendt. Du ser svaret her under Support.", "The message is sent. You will see the reply here under Support.")) }
                        .onFailure { error = it.message; requestRender() }
                }
            }
        }
    }
}
